import { track } from '@amplitude/analytics-browser';
import { formatDate } from '@angular/common';
import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ColDef, GridApi, GridReadyEvent } from "ag-grid-community";
import { ToastrService } from "ngx-toastr";
import { CommandRequestDetailsModalLinkComponent } from './command-request-details-modal-link/command-request-details-modal-link.component';
import { CommandRequestModalComponent } from './command-request-modal/command-request-modal.component';
import { CommandStatusComponent } from './command-status/command-status.component';
import { CommandRequestRecord, CommandRequestResponse, DeviceCommandSchedulerService } from "./services/device-command-scheduler.service";
import { IntervalService } from './services/interval.service';

@Component({
  selector: 'app-device-command',
  templateUrl: './device-command.component.html',
  styleUrls: ['./device-command.component.scss']
})
export class DeviceCommandComponent implements OnInit, OnDestroy {

  ngOnInit(): void {
    track('Control Center Device Command  page Loaded');
    this.resetPaginationTokenMap();
    this.startPolling();

    // When the caller (e.g. Device Battery's "Issue Commands" action) requests
    // it via query params, auto-open the Issue New Command modal with the
    // selected device serials pre-filled.
    const params = this.activatedRoute.snapshot.queryParamMap;
    if (params.get('openCommandModal') === 'true') {
      const serialNumbers = params.get('serialNumbers') ?? '';
      this.openModal(serialNumbers);
    }
  }

  ngOnDestroy() {
    this.stopPolling();
  }

  pageSize: number = 50;
  pageSizes: number[] = [50, 100, 150, 200]
  paginationTokenMap: Map<number, string> = new Map();
  firstPageToken: string = '{}';
  currentPage: number = 1;
  endOfSet: boolean;

  columnDefs: ColDef[] = [
    { field: 'COMMAND ID', width: 300, maxWidth: 300, cellRenderer: CommandRequestDetailsModalLinkComponent },
    { field: 'STATUS', width: 225, maxWidth: 225, cellRenderer: CommandStatusComponent },
    { field: 'DEVICE COUNT', width: 150, maxWidth: 150, cellRenderer: (params: any): string => { return params.data.deviceCount } },
    { field: 'COMMAND', cellRenderer: (params: any): string => { return this.getCommandName(params.data.command) } },
    { field: 'ISSUE DATE', cellRenderer: (params: any): string => { return formatDate(params.data.requestedDate, 'short', 'en-US') } },
    { field: 'ISSUER NAME', cellRenderer: (params: any): string => { return params.data.requesterName } },
    { field: 'DESCRIPTION', cellRenderer: (params: any): string => { return params.data.description } }
  ];

  defaultColDef: ColDef = {
    sortable: true,
    filter: false,
    resizable: true,
    suppressMovable: true
  };

  private gridApi!: GridApi<CommandRequestRecord>;
  private requests: CommandRequestRecord[] = [];

  constructor(
    private deviceCommandSchedulerService: DeviceCommandSchedulerService,
    private toastr: ToastrService,
    private modalService: NgbModal,
    private intervalService: IntervalService,
    private activatedRoute: ActivatedRoute,
  ) { }


  async onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;

    await this.getData();

    window.addEventListener('resize', function () {
      setTimeout(function () {
        params.api.sizeColumnsToFit();
      });
    });

    this.gridApi.sizeColumnsToFit();
  }
  async onRefresh() {
    this.gridApi.showLoadingOverlay();
    await this.getData();
    this.gridApi.hideOverlay();
    this.gridApi.sizeColumnsToFit();
  }
  async onRefreshPolling() {
    await this.getData();
    this.gridApi.sizeColumnsToFit();
  }

  async getData(): Promise<void> {
    try {
      let response = await this.deviceCommandSchedulerService.getRequests(this.pageSize, this.paginationTokenMap.get(this.currentPage));
      this.updatePageSettings(response);
      this.gridApi.setRowData(this.requests);
    } catch (error) {
      this.gridApi.setRowData([]);
      this.toastr.error('Failure retrieving data.');
    }
  }

  private updatePageSettings(response: CommandRequestResponse): void {
    this.endOfSet = response.endOfSet;
    this.requests = response?.commandRequests || [];
    if (!this.endOfSet) {
      this.paginationTokenMap.set(this.currentPage + 1, response.paginationToken);
    }
  }

  onPageSizeSelected(pageSize: number): void {
    this.resetPaginationTokenMap();
    this.currentPage = 1;
    this.pageSize = pageSize;
    this.onRefresh();
  }

  async onPageSelection(pageNumber: number): Promise<void> {
    if (this.currentPage === pageNumber) {
      return;
    }
    this.currentPage = pageNumber;
    await this.getData()
  }

  private resetPaginationTokenMap(): void {
    this.paginationTokenMap.clear();
    this.paginationTokenMap.set(1, this.firstPageToken);
  }

  async refreshAfterNewCommandRequest() {
    this.onRefreshPolling();//refresh immediately
    this.startPolling();
  }

  private getCommandName(command: string): string {
    switch (command) {
      case 'checkIn':
        return 'Check In'
      case 'wake':
        return 'Wake'
      case 'settingsUpdate':
        return 'Sync Settings'
      case 'reboot':
        return 'Reboot'
      case 'forceUpdate':
        return 'Force Update'
      default:
        return undefined;
    }
  }
  private startPolling() {
    this.intervalService.setInterval(async () => await this.onRefreshPolling(), 10000);
  }

  private stopPolling() {
    this.intervalService.clearInterval();
  }

  openModal(prefilledSerialNumbers: string = '') {
    this.stopPolling();
    const modal = this.modalService.open(CommandRequestModalComponent, { size: 'lg', windowClass: "lytxModal", centered: true });
    if (prefilledSerialNumbers) {
      modal.componentInstance.serialNumbers = prefilledSerialNumbers;
    }
    modal.componentInstance.refreshPage.subscribe(async () => await this.refreshAfterNewCommandRequest());
  }
}
